<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataHoraBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idClausulasComerciais', label: 'ID', width: 80, sortable: true },
  { field: 'titulo', label: 'Título', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'lstAplicacaoClausula', label: 'Aplicação', align: 'center', sortable: true, slotName: 'lstAplicacaoClausula' },
  { field: 'isDefinitiva', label: 'Definitiva', align: 'center', sortable: true, slotName: 'isDefinitiva' },
  { field: 'isFixa', label: 'Fixa', align: 'center', sortable: true, slotName: 'isFixa' },
  { field: 'ordemExibicao', label: 'Ordem', align: 'center', sortable: true },
  { field: 'lstStatusClausula', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'nomeUsuarioCriacao', label: 'Criado por', width: 150, sortable: false },
  { field: 'tsCriacao', label: 'Criado em', width: 150, sortable: true, slotName: 'tsCriacao' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstAplicacaoClausula: (row) => getAplicacaoTexto(row.lstAplicacaoClausula),
    lstStatusClausula: (row) => getStatusTexto(row.lstStatusClausula)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = ({ field: _field, order: _order }) => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({})

const filtros = reactive({
  busca: '',
  lstStatus: '',
  lstAplicacao: ''
})

const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('CLAUSULAS_COMERCIAIS')) return true
  return false
})

const podeVerAcoes = computed(() => temPermissaoCompleta.value)

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    Object.keys(listas).forEach(k => delete listas[k])
  }
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['ClausulasComerciais'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (filtros.lstAplicacao) params.append('lstAplicacao', filtros.lstAplicacao)

    const response = await api.get(`/ClausulasComerciais?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar as cláusulas comerciais.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatus = ''
  filtros.lstAplicacao = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/ClausulasComerciais/${item.idClausulasComerciais}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da cláusula.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idClausulasComerciais
  auditoriaSelecionada.nome = item.titulo
  mostrarAuditoria.value = true
}

const getAplicacaoTexto = (idAplicacao) => {
  const aplicacaoObj = (listas.LstAplicacaoClausula || []).find(s => s.idListaClienteItem === idAplicacao)
  return aplicacaoObj ? aplicacaoObj.textoLista : idAplicacao
}

const getStatusTexto = (idStatus) => {
  const statusObj = (listas.LstStatusClausula || []).find(s => s.idListaClienteItem === idStatus)
  return statusObj ? statusObj.textoLista : idStatus
}

const obterCorStatus = (idStatus) => {
  const texto = String(getStatusTexto(idStatus)).toUpperCase()

  if (texto.includes('ATIVO') || texto.includes('APROVADO') || texto.includes('CONCLUÍDO') || texto.includes('FINALIZADO')) {
    return 'badge-ghost-success'
  }
  if (texto.includes('ABERTO') || texto.includes('PENDENTE') || texto.includes('DIGITAÇÃO')) {
    return 'badge-ghost-warning'
  }
  if (texto.includes('CANCELADO') || texto.includes('INATIVO') || texto.includes('REPROVADO')) {
    return 'badge-ghost-danger'
  }
  if (texto.includes('ANÁLISE') || texto.includes('ENVIADO')) {
    return 'badge-ghost-info'
  }
  if (texto.includes('PROCESSANDO')) {
    return 'badge-ghost-primary'
  }

  return 'badge-ghost-secondary'
}

const obterBadgeBooleano = (valor) => {
  return valor ? 'badge-ghost-success' : 'badge-ghost-secondary'
}

const textoBooleano = (valor) => {
  return valor ? 'Sim' : 'Não'
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    titulo="Gestão de Cláusulas Comerciais"
    subtitulo="Administração de cláusulas contratuais padrão da empresa"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Buscar</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar por título ou texto..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in (listas.LstStatusClausula || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Aplicação</label>
          <select class="form-select" v-model="filtros.lstAplicacao" @change="aplicarFiltros">
            <option value="">Todas as Aplicações</option>
            <option v-for="a in (listas.LstAplicacaoClausula || [])" :key="a.idListaClienteItem" :value="a.idListaClienteItem">
              {{ a.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma cláusula comercial encontrada."
      row-key="idClausulasComerciais"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #lstAplicacaoClausula="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getAplicacaoTexto(row.lstAplicacaoClausula) }}
        </span>
      </template>

      <template #isDefinitiva="{ row }">
        <span class="badge rounded-pill" :class="obterBadgeBooleano(row.isDefinitiva)">
          {{ textoBooleano(row.isDefinitiva) }}
        </span>
      </template>

      <template #isFixa="{ row }">
        <span class="badge rounded-pill" :class="obterBadgeBooleano(row.isFixa)">
          {{ textoBooleano(row.isFixa) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusClausula)">{{ getStatusTexto(row.lstStatusClausula) }}</span>
      </template>

      <template #tsCriacao="{ row }">
        {{ formatarDataHoraBR(row.tsCriacao) }}
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" title="Editar">
            <i class="bi bi-pencil-fill text-primary"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ClausulasComerciais"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>
